import { jwtDecode } from 'jwt-decode';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ErrorGraphQLType } from '@trendiolive/trendio-selfcare-ui-kit';

import { AuthState, User } from 'model/types/auth';

import { authenticatedPost, setSession } from 'utils/axios';

import { USER_LOGIN } from 'queries/login';
import { GET_USER_INITIAL_INFO } from 'queries/get_user_initial_info';

export const initialState: AuthState = {
  isRoleAttached: false,
  isLoggedIn: false,
  flags: [],
  hasCampaignPlan: false,
  tableVisibilityPrefs: null,
  token: null,
  user: null,
  userTokenInfo: null
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login(state, action: PayloadAction<AuthState>) {
      state.isRoleAttached = false;
      state.isLoggedIn = true;
      state.token = action.payload.token;
    },
    logout(state) {
      resetAuthState(state);
    },
    setUser(state, action: PayloadAction<AuthState>) {
      let tokenInfo: any = decodedToken({ stateToken: state.token, actionToken: action.payload.token });
      const isOldToken = !tokenInfo?.['iss'];
      const userWithoutRoles = !action.payload.user?.roles || action.payload.user.roles === null || action.payload.user.roles?.length === 0;
      if (isOldToken || userWithoutRoles) {
        resetAuthState(state);
      } else {
        state.flags = action.payload.flags;
        state.hasCampaignPlan = action.payload.hasCampaignPlan;
        state.isRoleAttached = !userWithoutRoles;
        state.isLoggedIn = !!action.payload.user;
        state.user = action.payload.user;
        state.userTokenInfo = tokenInfo;
      }
    },
    prepareMerchantAttach(state) {
      state.isRoleAttached = false;
    }
  }
});

export const { login, logout, prepareMerchantAttach, setUser } = authSlice.actions;

export async function userLogin(email: string, password: string): Promise<{ authState: AuthState; errors?: ErrorGraphQLType[] }> {
  const response = await authenticatedPost({
    query: USER_LOGIN,
    vars: {
      email,
      password
    }
  });
  if (response.data.login) {
    const { token } = response.data.login;
    setSession(token);
    const state: AuthState = {
      token: token,
      userTokenInfo: jwtDecode(token)
    };
    return { authState: state };
  }
  let errors = response.errors;
  return { authState: { token: null }, errors };
}

export async function queryUser(): Promise<AuthState> {
  const response = await authenticatedPost({
    query: GET_USER_INITIAL_INFO
  });
  let user: User | null = null;
  let flags: string[] = [];
  let hasCampaignPlan = false;
  if (response.data?.campaignPlans && response.data?.campaignPlans?.edges.length > 0) {
    hasCampaignPlan = !!response.data.campaignPlans.edges[0].node?.id;
  }
  if (response.data?.me) {
    user = response.data.me;
  }
  if (response.data?.waffle?.flags) {
    flags = response.data.waffle.flags.filter((flag: any) => flag.isActive).map((flag: any) => flag.name);
  }
  return { hasCampaignPlan, flags, user };
}

function resetAuthState(state: AuthState): void {
  state.isRoleAttached = true;
  state.isLoggedIn = false;
  state.user = null;
  setSession(null);
}

// TODO: we need to figure out something about this any
function decodedToken({ stateToken, actionToken }: { stateToken?: string | null; actionToken?: string | null }): any | null {
  let tokenInfo: any = null;
  if (actionToken) {
    tokenInfo = jwtDecode(actionToken);
  } else if (stateToken) {
    tokenInfo = jwtDecode(stateToken);
  }
  return tokenInfo;
}
